// law_snappy — what this person actually opens, so the shell preloads the right things.
//
// The dashboard splits its rarer parts (the editor, the orbit, the timeline, the
// share studio, the pickers…) into chunks that load on first use. Loading them
// on use costs a beat the first time; loading ALL of them up front would put
// every one on the critical path for everybody. The middle road is the one a
// native app takes with its caches: remember what this person reaches for, and
// warm exactly those while the machine is idle. Someone who edits every day has
// the editor ready before they press edit. Someone who has never opened the
// orbit never downloads it.
//
// Per-browser (localStorage), counts + a last-used time only. It records which
// kinds of surface were opened, never what was in them, and it survives only
// as long as the browser keeps it. Every read and write is guarded: private
// windows and blocked storage simply fall back to "nothing warmed".

const KEY = "innernet:usage:v1";
// a feature not touched in this long is no longer "yours" and stops being warmed
const STALE_MS = 21 * 86400_000;

type Ledger = Record<string, { n: number; at: number }>;

function read(): Ledger {
  try {
    const raw = window.localStorage.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : null;
    return v && typeof v === "object" ? (v as Ledger) : {};
  } catch {
    return {};
  }
}

/** note that a feature was opened (cheap; call it at the moment of use). */
export function noteUse(feature: string): void {
  if (typeof window === "undefined") return;
  try {
    const l = read();
    const prev = l[feature];
    l[feature] = { n: Math.min((prev?.n ?? 0) + 1, 9999), at: Date.now() };
    window.localStorage.setItem(KEY, JSON.stringify(l));
  } catch {
    /* storage blocked — nothing to remember */
  }
}

/** has this person used the feature recently (at least `min` times)? */
export function usedRecently(feature: string, min = 1): boolean {
  if (typeof window === "undefined") return false;
  const e = read()[feature];
  return !!e && e.n >= min && Date.now() - e.at < STALE_MS;
}

type IdleWindow = Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };

/** run `cb` when the main thread is idle (falls back to a short timeout). */
export function whenIdle(cb: () => void, timeout = 4000): void {
  if (typeof window === "undefined") return;
  const w = window as IdleWindow;
  if (w.requestIdleCallback) w.requestIdleCallback(cb, { timeout });
  else setTimeout(cb, 400);
}

/** warm, one per idle slot, every loader whose feature this person uses. */
export function warmByUsage(loaders: Record<string, () => Promise<unknown>>): void {
  const queue = Object.entries(loaders).filter(([f]) => usedRecently(f));
  const next = () => {
    const item = queue.shift();
    if (!item) return;
    item[1]().catch(() => { /* a failed warm just means it loads on use */ });
    whenIdle(next);
  };
  whenIdle(next);
}
